import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { expandLevel2WithRaw } from '../../util/search';

const FETCH_LISTINGS_REQUEST = 'app/landingPage/FETCH_LISTINGS_REQUEST';
const FETCH_LISTINGS_SUCCESS = 'app/landingPage/FETCH_LISTINGS_SUCCESS';
const FETCH_LISTINGS_ERROR = 'app/landingPage/FETCH_LISTINGS_ERROR';

const SECTION_KEYS = {
  recent: {
    listingsKey: 'listings',
    loadingKey: 'loading',
    errorKey: 'error',
  },
  mostViewed: {
    listingsKey: 'mostViewedListings',
    loadingKey: 'mostViewedLoading',
    errorKey: 'mostViewedError',
  },
  mostWatched: {
    listingsKey: 'mostWatchedListings',
    loadingKey: 'mostWatchedLoading',
    errorKey: 'mostWatchedError',
  },
  // vintage: {
  //   listingsKey: 'vintageListings',
  //   loadingKey: 'vintageLoading',
  //   errorKey: 'vintageError',
  // },
  baseball: {
    listingsKey: 'baseballListings',
    loadingKey: 'baseballLoading',
    errorKey: 'baseballError',
  },
  basketball: {
    listingsKey: 'basketballListings',
    loadingKey: 'basketballLoading',
    errorKey: 'basketballError',
  },
  football: {
    listingsKey: 'footballListings',
    loadingKey: 'footballLoading',
    errorKey: 'footballError',
  },
  soccer: {
    listingsKey: 'soccerListings',
    loadingKey: 'soccerLoading',
    errorKey: 'soccerError',
  },
  hockey: {
    listingsKey: 'hockeyListings',
    loadingKey: 'hockeyLoading',
    errorKey: 'hockeyError',
  },
};

const initialState = {
  listings: [],
  loading: false,
  error: null,
  mostViewedListings: [],
  mostViewedLoading: false,
  mostViewedError: null,
  // vintageListings: [],
  // vintageLoading: false,
  // vintageError: null,
  baseballListings: [],
  baseballLoading: false,
  baseballError: null,
  basketballListings: [],
  basketballLoading: false,
  basketballError: null,
  footballListings: [],
  footballLoading: false,
  footballError: null,
  soccerListings: [],
  soccerLoading: false,
  soccerError: null,
  hockeyListings: [],
  hockeyLoading: false,
  hockeyError: null,
  mostWatchedListings: [],
  mostWatchedLoading: false,
  mostWatchedError: null,
};

export default function LandingPageReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_LISTINGS_REQUEST: {
      const section = action.meta?.section || 'recent';
      const sectionKeys = SECTION_KEYS[section] || SECTION_KEYS.recent;
      return {
        ...state,
        [sectionKeys.loadingKey]: true,
        [sectionKeys.errorKey]: null,
      };
    }
    case FETCH_LISTINGS_SUCCESS: {
      const section = action.meta?.section || 'recent';
      const sectionKeys = SECTION_KEYS[section] || SECTION_KEYS.recent;
      return {
        ...state,
        [sectionKeys.loadingKey]: false,
        [sectionKeys.listingsKey]: action.payload,
      };
    }
    case FETCH_LISTINGS_ERROR: {
      const section = action.meta?.section || 'recent';
      const sectionKeys = SECTION_KEYS[section] || SECTION_KEYS.recent;
      return {
        ...state,
        [sectionKeys.loadingKey]: false,
        [sectionKeys.errorKey]: action.error,
      };
    }
    default:
      return state;
  }
}

const createBaseQueryParams = config => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = 16 / 9;

  return {
    minStock: 1,
    pub_recentlySold: false,
    include: ['author', 'images'],
    perPage: 12,
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.default',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 240, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 480, aspectRatio),
  };
};

export const fetchListings = (config, section = 'recent', extraQuery = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch({ type: FETCH_LISTINGS_REQUEST, meta: { section } });

  const queryParams = {
    ...createBaseQueryParams(config),
    ...extraQuery,
  };

  try {
    const listingsCall = await sdk.listings.query(queryParams);
    const listings = listingsCall.data.data;
    const listingRefs = listings.map(({ id, type }) => ({ id, type }));
    dispatch(addMarketplaceEntities(listingsCall));
    dispatch({ type: FETCH_LISTINGS_SUCCESS, payload: listingRefs, meta: { section } });
    return listingsCall;
  } catch (error) {
    console.error('Error fetching listings:', error);
    dispatch({ type: FETCH_LISTINGS_ERROR, error, meta: { section } });
    return null;
  }
};

export const ASSET_NAME = 'landing-page';

export const fetchRecentListings = config => fetchListings(config, 'recent');

export const loadData = (params, search, config) => dispatch => {
  const listingPromises = [
    dispatch(fetchListings(config, 'recent')),
    dispatch(fetchListings(config, 'mostViewed', { sort: 'pub_totalViews' })),
    dispatch(fetchListings(config, 'mostWatched', { sort: 'pub_totalLikes' })),
    // dispatch(fetchListings(config, 'vintage', { pub_yearcard: '0,1989' })),
    dispatch(fetchListings(config, 'baseball', { pub_level2: expandLevel2WithRaw('baseballcards') })),
    dispatch(fetchListings(config, 'basketball', { pub_level2: expandLevel2WithRaw('basketballcards') })),
    dispatch(fetchListings(config, 'football', { pub_level2: expandLevel2WithRaw('footballcards') })),
    dispatch(fetchListings(config, 'soccer', { pub_level2: expandLevel2WithRaw('soccercards') })),
    dispatch(fetchListings(config, 'hockey', { pub_level2: expandLevel2WithRaw('hockeycards') })),
  ];
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };

  return Promise.all([Promise.all(listingPromises), dispatch(fetchPageAssets(pageAsset, true))]);
};
